export const simpleHash = (s) => { let h=0; for(let i=0;i<s.length;i++) h=(Math.imul(31,h)+s.charCodeAt(i))|0; return h.toString(36); };
export const today = () => new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});

// Turn a store_slug into a valid DNS subdomain label (lowercase a-z0-9-, no leading/
// trailing hyphen, ≤63 chars) for storename.bloomhq.in links. e.g. "aesthkeys-" → "aesthkeys".
export const slugToSubdomain = (slug) =>
  (slug || "").toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);

export const cleanInstaHandle = (val) => {
  if (!val) return "";
  let v = val.trim();
  try {
    if (v.startsWith("http")) {
      const url = new URL(v);
      v = url.pathname.replace(/^\//, "").split("/")[0];
    }
  } catch {}
  return v.replace(/^@/, "").split("?")[0].trim();
};

// Country value from the address form. Browser autofill (Android Chrome, Samsung/Google autofill, Instagram's in-app
// browser) can write "IN", "IND", "india" or a value that matches NO option into the country <select>. React then holds
// "" while the dropdown still LOOKS like India, and the checkout treated that as an international order ("<store> ships
// within India only"). An empty value is India (the default); India-like spellings become exactly "India"; a known
// country is matched case-insensitively to its list entry; anything else is returned trimmed so it is never silently
// turned into India.
export const normCountry = (v, list = []) => {
  const s = String(v ?? "").trim();
  if (!s) return "India";
  if (/^(in|ind|india|bharat|republic of india)$/i.test(s)) return "India";
  const hit = (list || []).find(c => String(c).toLowerCase() === s.toLowerCase());
  return hit || s;
};

// Indian state from the address form. Autofill writes "WB", "west bengal", "Orissa", "NCT of Delhi" … into the state
// <select>; none of those match an option, so the dropdown went blank, the per-state shipping rate (keyed by the exact
// state name) was missed and the saved address carried the odd text. Match case-insensitively (treating "&" and "and"
// as the same), accept the 2-letter codes and the old/alternative names, and return the exact list entry. Anything we
// don't recognise is returned trimmed (never guessed).
const _ST_CODES = { AP:"Andhra Pradesh", AR:"Arunachal Pradesh", AS:"Assam", BR:"Bihar", CG:"Chhattisgarh", CT:"Chhattisgarh", GA:"Goa", GJ:"Gujarat", HR:"Haryana", HP:"Himachal Pradesh", JH:"Jharkhand", KA:"Karnataka", KL:"Kerala", MP:"Madhya Pradesh", MH:"Maharashtra", MN:"Manipur", ML:"Meghalaya", MZ:"Mizoram", NL:"Nagaland", OD:"Odisha", OR:"Odisha", PB:"Punjab", RJ:"Rajasthan", SK:"Sikkim", TN:"Tamil Nadu", TS:"Telangana", TG:"Telangana", TR:"Tripura", UP:"Uttar Pradesh", UK:"Uttarakhand", UT:"Uttarakhand", UA:"Uttarakhand", WB:"West Bengal", AN:"Andaman & Nicobar Islands", CH:"Chandigarh", DN:"Dadra & Nagar Haveli and Daman & Diu", DD:"Dadra & Nagar Haveli and Daman & Diu", DH:"Dadra & Nagar Haveli and Daman & Diu", DL:"Delhi", JK:"Jammu & Kashmir", LA:"Ladakh", LD:"Lakshadweep", PY:"Puducherry" };
const _ST_ALIASES = { "orissa":"Odisha", "uttaranchal":"Uttarakhand", "pondicherry":"Puducherry", "nct of delhi":"Delhi", "new delhi":"Delhi", "delhi ncr":"Delhi", "telengana":"Telangana", "dadra and nagar haveli":"Dadra & Nagar Haveli and Daman & Diu", "daman and diu":"Dadra & Nagar Haveli and Daman & Diu", "daman & diu":"Dadra & Nagar Haveli and Daman & Diu", "dadra & nagar haveli":"Dadra & Nagar Haveli and Daman & Diu" };
export const normState = (v, list = []) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const key = (x) => String(x).toLowerCase().replace(/\s*&\s*/g, " and ").replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
  const k = key(s);
  const hit = (list || []).find(c => key(c) === k);
  if (hit) return hit;
  const named = _ST_ALIASES[k] || _ST_ALIASES[s.toLowerCase()];
  if (named && (!list.length || list.includes(named))) return named;
  const code = _ST_CODES[s.toUpperCase().replace(/[^A-Z]/g, "")];
  if (code && s.replace(/[^A-Za-z]/g, "").length === 2 && (!list.length || list.includes(code))) return code;
  return s;
};

// Indian pincode: autofill/paste can bring "700 026" or "700-026". Strip spaces/hyphens when that leaves exactly 6 digits;
// other countries (UK "SW1A 1AA", Canada "K1A 0B1" …) keep their spacing.
export const normPincode = (country, v) => {
  const s = String(v ?? "").trim();
  if (String(country) !== "India") return s;
  const d = s.replace(/[\s-]/g, "");
  return /^\d{6}$/.test(d) ? d : s;
};

// Effective shipping after a store's free-shipping rules.
// base: the store's shipping_cost. subtotal/qty: this store's item total + count.
// freeAmount / freeQty: thresholds (0 = disabled). If either threshold is met, ship free.
// surplusAfterQty / surplusAmount ("shipping surplus", 0 = off): when the order has MORE THAN
// surplusAfterQty items, a flat surplusAmount is added ONCE on top of the shipping that is
// charged. It never applies when shipping is free (free rules / free base) and never to
// international orders (those use the flat intl rate and don't go through this helper).
// Mirrored in bloom-backend/shipping.js (tested against each other).
export const effectiveShipping = (base, subtotal, qty, freeAmount = 0, freeQty = 0, surplusAfterQty = 0, surplusAmount = 0) => {
  base = Number(base) || 0;
  if (base <= 0) return 0;
  const a = Number(freeAmount) || 0;
  const q = Number(freeQty) || 0;
  if (a > 0 && Number(subtotal) >= a) return 0;
  if (q > 0 && Number(qty) >= q) return 0;
  const sq = Number(surplusAfterQty) || 0;
  const sa = Number(surplusAmount) || 0;
  if (sq > 0 && sa > 0 && Number(qty) > sq) return base + sa;
  return base;
};

// ── Bundle offer "N for ₹X" (e.g. 3 for ₹999) — mirrors bundleSubtotal() in
// bloom-backend. Applies ONCE: the most expensive N units are priced together at X;
// every unit beyond the first N is at its full price (NOT repeating).
export const bundleSubtotal = (items, n, price) => {
  if (!(n >= 1) || !(price >= 0)) return null;
  const units = [];
  for (const it of (items || [])) {
    const p = Number(it.price || 0), q = Math.max(1, Number(it.qty || 1));
    for (let k = 0; k < q; k++) units.push(p);
  }
  if (units.length < n) return null;
  units.sort((a, b) => b - a);
  const rest = units.slice(n).reduce((s, p) => s + p, 0);
  return price + rest;
};
// Discount (₹) a store's active bundle_offer gives for these items, else 0.
export const bundleDiscount = (storeInfo, items) => {
  const bo = storeInfo?.bundle_offer;
  if (!bo || bo.active === false) return 0;
  const full = (items || []).reduce((s, i) => s + Number(i.price || 0) * Number(i.qty || 1), 0);
  const bundled = bundleSubtotal(items, Number(bo.min_qty), Number(bo.price));
  if (bundled == null || bundled >= full) return 0;
  return full - bundled;
};

// ── BOGO "Buy X get Y free" ───────────────────────────────────────────────────
// MUST stay in lockstep with bogoDiscountFor() in bloom-backend/server.js — the
// server recomputes this from DB truth and is authoritative at checkout.
//
// Rules (deliberate, and what the storefront copy promises):
//  • Only products the seller toggled ON (bogo_eligible) take part.
//  • Every (buy + get) eligible UNITS forms one set; it repeats — buy 2 get 1 on
//    6 eligible units = 2 free.
//  • The CHEAPEST units in the qualifying sets are the free ones. Standard retail
//    behaviour, and it stops a shopper pairing one cheap item with an expensive
//    one to get the expensive one free.
export const bogoConfig = (storeInfo) => {
  const b = storeInfo?.bogo_offer;
  if (!b || b.active === false) return null;
  const buy = Math.floor(Number(b.buy));
  const get = Math.floor(Number(b.get));
  if (!(buy >= 1) || !(get >= 1)) return null;
  return { buy, get };
};

// Human label, e.g. "Buy 2 Get 1 Free". Used on badges everywhere.
export const bogoLabel = (storeInfo) => {
  const c = bogoConfig(storeInfo);
  return c ? `Buy ${c.buy} Get ${c.get} Free` : "";
};

export const isBogoItem = (item) => item?.bogo_eligible === true;

// Discount (₹) a store's active BOGO gives for these items, else 0.
export const bogoDiscount = (storeInfo, items) => {
  const cfg = bogoConfig(storeInfo);
  if (!cfg) return 0;
  const setSize = cfg.buy + cfg.get;
  const units = [];
  for (const it of (items || [])) {
    if (!isBogoItem(it)) continue;
    const p = Number(it.price || 0);
    const q = Math.max(1, Math.floor(Number(it.qty || 1)));
    for (let k = 0; k < q; k++) units.push(p);
  }
  if (units.length < setSize) return 0;
  const sets = Math.floor(units.length / setSize);
  const freeCount = sets * cfg.get;
  units.sort((a, b) => a - b);                       // cheapest first — those are free
  return units.slice(0, freeCount).reduce((s, p) => s + p, 0);
};

// How many free units the shopper is getting right now (for the cart line copy).
export const bogoFreeCount = (storeInfo, items) => {
  const cfg = bogoConfig(storeInfo);
  if (!cfg) return 0;
  const setSize = cfg.buy + cfg.get;
  const qty = (items || []).reduce((s, i) => s + (isBogoItem(i) ? Math.max(1, Number(i.qty || 1)) : 0), 0);
  return Math.floor(qty / setSize) * cfg.get;
};

// Units still needed for the NEXT free item — powers "add 1 more to get 1 free".
export const bogoUnitsToNext = (storeInfo, items) => {
  const cfg = bogoConfig(storeInfo);
  if (!cfg) return 0;
  const setSize = cfg.buy + cfg.get;
  const qty = (items || []).reduce((s, i) => s + (isBogoItem(i) ? Math.max(1, Number(i.qty || 1)) : 0), 0);
  if (qty === 0) return cfg.buy;                     // nothing yet → need the buy-side
  const into = qty % setSize;
  return into === 0 ? 0 : Math.max(0, cfg.buy - into);
};

// ── Checkout-hold availability helpers (shared by Storefront + Marketplace) ────
// buildHeldMap turns /api/held-stock rows ([{product_id, variation_name, held}]) into a
// Map keyed by productId (base) and `${productId}::${variationName}` (per variation).
export const buildHeldMap = (rows) => {
  const m = new Map();
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (!r?.product_id) continue;
    const key = r.variation_name ? `${r.product_id}::${r.variation_name}` : r.product_id;
    m.set(key, (m.get(key) || 0) + (Number(r.held) || 0));
  }
  return m;
};

// applyHolds subtracts active checkout holds from physical stock so a list of products
// shows true availability ("available = physical − holds"). Variation-aware. Returns the
// list unchanged when there are no holds, so it's a safe no-op on the empty-map fallback.
export const applyHolds = (products, heldMap) => {
  if (!Array.isArray(products) || !products.length || !heldMap || heldMap.size === 0) return products;
  return products.map(p => {
    const baseHeld = heldMap.get(p.id) || 0;
    const hasVars = Array.isArray(p.variations) && p.variations.length > 0;
    if (hasVars) {
      const variations = p.variations.map(v => {
        const h = heldMap.get(`${p.id}::${v.name}`) || 0;
        return h ? { ...v, stock: Math.max(0, (v.stock || 0) - h) } : v;
      });
      let stock = Math.max(0, (p.stock || 0) - baseHeld);
      if (variations.every(v => (v.stock || 0) === 0)) stock = 0;
      return { ...p, variations, stock };
    }
    return baseHeld ? { ...p, stock: Math.max(0, (p.stock || 0) - baseHeld) } : p;
  });
};
